import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Output,
    viewChildren,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {LoadComponent} from '@brunata-angular/ui';
import {
    formatStreetAddress,
    fromControl,
    keyComparator,
    numericLocaleComparator,
    TranslatePipe,
} from '@brunata-angular/util';
import {BuildingService} from '@shared/services/building.service';
import {BehaviorSubject, combineLatest, map, tap} from 'rxjs';
import {FormBuilder, ReactiveFormsModule} from '@angular/forms';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {applySortingToHeaders, SortableTableHeaderComponent, Sorting} from '@brunata-angular/ui';
import {NgbPopover} from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-select-building-table',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        LoadComponent,
        TranslatePipe,
        InputTextGroupComponent,
        ReactiveFormsModule,
        SortableTableHeaderComponent,
        NgbPopover,
    ],
    templateUrl: './select-building-table.component.html',
    styleUrl: './select-building-table.component.scss',
})
export class SelectBuildingTableComponent {
    @Output() loadBuilding = new EventEmitter<{buildingNo: string}>();
    private readonly headers = viewChildren(SortableTableHeaderComponent);
    private sorting = new BehaviorSubject<Sorting>({
        column: 'buildingNo',
        direction: 'asc',
    });

    private readonly buildingService = inject(BuildingService);
    private readonly formBuilder = inject(FormBuilder);
    formGroup = this.formBuilder.group({
        search: [''],
    });

    readonly filteredBuildings$ = combineLatest([
        this.buildingService.getBuildings(),
        fromControl(this.formGroup),
    ]).pipe(
        map(([buildings, form]) => {
            const search = form.search?.toLowerCase().trim() ?? null;

            const transformedBuildings = buildings.map(building => ({
                ...building,
                address: formatStreetAddress(building.building),
            }));
            return transformedBuildings.filter(building => {
                if (search == null) {
                    return true;
                }

                const address = building.address.toLowerCase();
                const buildingName = building.building.buildingName?.toLowerCase() ?? '';
                const buildingNo = building.building.buildingNo;
                const buildingIdPairs = building.buildingIdPairs ?? '';
                return (
                    address.includes(search) ||
                    buildingName.includes(search) ||
                    buildingNo.includes(search) ||
                    buildingIdPairs.toString().includes(search)
                );
            });
        }),
        tap(() => {
            setTimeout(() => {
                this.onSort({column: 'buildingNo', direction: 'asc'});
            }, 0);
        }),
    );

    readonly buildings$ = combineLatest([this.filteredBuildings$, this.sorting]).pipe(
        map(([transformedBuildings, sorting]) =>
            transformedBuildings.sort(
                sorting.column === 'address'
                    ? keyComparator(
                          building => building.address,
                          sorting.direction != null ? sorting.direction : 'asc',
                      )
                    : sorting.column === 'buildingIdPairs'
                      ? keyComparator(
                            building => building.buildingIdPairs?.join(','),
                            sorting.direction != null ? sorting.direction : 'asc',
                        )
                      : sorting.column === 'buildingName'
                        ? keyComparator(
                              building => building.building.buildingName,
                              sorting.direction != null ? sorting.direction : 'asc',
                          )
                        : numericLocaleComparator(
                              building => building.building.buildingNo,
                              sorting.direction != null ? sorting.direction : 'asc',
                          ),
            ),
        ),
    );

    selectBuilding(buildingNo: string): void {
        this.loadBuilding.emit({buildingNo: buildingNo});
    }

    onSort(sorting: Sorting): void {
        this.sorting.next(sorting);
        applySortingToHeaders(this.headers(), sorting);
    }
}
